import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { adminService, VipPricingAdmin } from '../services/api';

const AdminVipPricing: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalPriceBrl, setOriginalPriceBrl] = useState('');
  const [promoPriceBrl, setPromoPriceBrl] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [asyncPvPLobbyMinJoinableRooms, setAsyncPvPLobbyMinJoinableRooms] = useState('');
  const [discountPercent, setDiscountPercent] = useState<number | null>(null);
  const [updatedAtUtc, setUpdatedAtUtc] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getVipPricing();
      applyData(data);
    } catch {
      setError('Could not load VIP pricing.');
    } finally {
      setLoading(false);
    }
  };

  const applyData = (data: VipPricingAdmin) => {
    setOriginalPriceBrl(String(data.originalPriceBrl));
    setPromoPriceBrl(String(data.promoPriceBrl));
    setDurationDays(String(data.durationDays));
    setAsyncPvPLobbyMinJoinableRooms(String(data.asyncPvPLobbyMinJoinableRooms));
    setDiscountPercent(data.discountPercent);
    setUpdatedAtUtc(data.updatedAtUtc);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    const orig = parseFloat(originalPriceBrl.replace(',', '.'));
    const promo = parseFloat(promoPriceBrl.replace(',', '.'));
    const days = parseInt(durationDays, 10);
    const minRooms = parseInt(asyncPvPLobbyMinJoinableRooms, 10);
    if (
      Number.isNaN(orig) ||
      Number.isNaN(promo) ||
      Number.isNaN(days) ||
      Number.isNaN(minRooms)
    ) {
      setError('Enter valid numbers.');
      setSaving(false);
      return;
    }
    try {
      const data = await adminService.updateVipPricing({
        originalPriceBrl: orig,
        promoPriceBrl: promo,
        durationDays: days,
        asyncPvPLobbyMinJoinableRooms: minRooms,
      });
      applyData(data);
      setSuccess('VIP pricing saved.');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not save VIP pricing.';
      setError(typeof msg === 'string' ? msg : 'Could not save VIP pricing.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      <h3>VIP pricing</h3>
      <p className="text-muted">
        Values shown on the public VIP page (list price, promotional price, duration). Discount % is calculated
        automatically.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="vip-original">
              <Form.Label>Original price (BRL)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={originalPriceBrl}
                onChange={(e) => setOriginalPriceBrl(e.target.value)}
                required
              />
              <Form.Text className="text-muted">Shown as strikethrough (e.g. list price before promo).</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="vip-promo">
              <Form.Label>Promotional price (BRL)</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={promoPriceBrl}
                onChange={(e) => setPromoPriceBrl(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="vip-days">
              <Form.Label>Duration (days)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={3650}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="min-rooms">
              <Form.Label>Min Open Rooms for Matchmaking Deferral (0 to disable)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={100}
                value={asyncPvPLobbyMinJoinableRooms}
                onChange={(e) => setAsyncPvPLobbyMinJoinableRooms(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Minimum target open rooms per quiz to seed in Arena. Set to 0 to disable seeding and force immediate matchmaking.
              </Form.Text>
            </Form.Group>
            {discountPercent != null && (
              <p className="small text-muted mb-3">
                Computed discount badge: <strong>{discountPercent}% OFF</strong>
                {updatedAtUtc && (
                  <>
                    {' '}
                    · Last updated: {new Date(updatedAtUtc).toLocaleString()}
                  </>
                )}
              </p>
            )}
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminVipPricing;
